import { Component, inject, OnDestroy, OnInit, AfterViewInit, TemplateRef, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  FormSelectorBaseComponent,
  Instance,
  InstanceResolveData,
  LoadingService,
  ViewType,
  getDeepestRouteSnapshot,
} from '@finra/rampdi-forms-common';
import { OverlayService, formatDate } from '@finra/forms';
import { formatAddressBlock as formatAddressBlockUtil } from '../../utils/address.utils';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom, forkJoin } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

import { CompletenessCheckService } from '../../services/completeness-check.service';
import { InstanceService, MU3WithSupportingData } from '../../services/instance.service';
import { NavigationDataService } from '../../services/navigation-data.service';
import { RedlineService } from '../../services/redline.service';
import { SchemaService } from '../../services/schema.service';
import { DisplayRulesService, FilingStatusDisplayRules } from '../../services/display-rules.service';
import { GlobalContext } from '../../services/global-context.service';
import { environment } from '../../../environments/environment';
import { AddressFormData } from '../../models/address.model';
import { CONTROL_CHARS_LAYOUT_VALIDATORS } from '../../validators/control-chars.validator';
import { AddressHelperService, AddressVerificationStatus } from '../../services/address-helper.service';

@Component({
  selector: 'app-form-selector',
  templateUrl: './form-selector.component.html',
  styleUrls: ['./form-selector.component.scss'],
  providers: [],
})
export class FormSelectorComponent
  extends FormSelectorBaseComponent<
    MU3WithSupportingData,
    Instance<any>,
    InstanceResolveData<MU3WithSupportingData, Instance<any>>
  >
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('updateEmailModal') updateEmailModal: TemplateRef<any>;
  @ViewChild('addressEditModal', { static: true }) addressEditModal: TemplateRef<any>;
  addressFormData: AddressFormData = {};

  /** Delay (ms) before restoring focus after a modal closes, allowing the overlay to fully tear down. */
  private static readonly FOCUS_RESTORE_DELAY_MS = 100;

  /** Delay (ms) before auto-closing the mailing modal after Save/Accept/Reject resolves, so the result renders first. */
  private static readonly AUTO_SAVE_DELAY_MS = 300;

  private DRS = inject(DisplayRulesService);
  private GC = inject(GlobalContext);
  private isDestroying$ = new Subject<void>();

  displayRules$: Observable<FilingStatusDisplayRules> = this.DRS.displayRules$;

  hasReadOnlyMode$$ = toSignal(
    this.displayRules$.pipe(
      takeUntil(this.isDestroying$),
      map((res) => res && Object.keys(res)?.length > 0 && res['readOnlyMode']),
    ),
  );

  permissions$$ = toSignal(
    this.displayRules$.pipe(
      takeUntil(this.isDestroying$),
      map((res) => res),
    ),
  );

  showValidationOnCC$$ = toSignal(
    this.CC.completenessCheck$.pipe(
      takeUntil(this.isDestroying$),
      map((resp) => (resp?.['open'] ? resp['open'] : false)),
    ),
  );

  canAttestResp$$ = toSignal(this.DRS.canAttestResp$.pipe(takeUntil(this.isDestroying$)));

  // Cached reference data for readonly address label resolution
  private stateOptions: any[] = [];
  private countryOptions: any[] = [];
  referenceDataLoaded = false;

  isAddressFormInvalid(model: any): boolean {
    if (!model) return true;
    const addr = model.address || model;
    // Postal Code is required for every address -- US and International alike (parity
    // with Classic). Only the *format* (xxxxx / xxxxx-xxxx) and State are US-specific;
    // international postal codes are accepted as entered with no pattern validation.
    // Whitespace-only is treated as empty to match the CC rules, which use the
    // blank-string helpers (mailingPostalCodeRequired -> #isNonBlankString,
    // expensePostalCodeRequired -> #isBlankString) -- otherwise Save would succeed
    // here and CC would reject on submit.
    const postalCode = (addr.postalCode ?? '').toString().trim();
    const baseInvalid = !addr.street1 || !addr.city || !addr.country || !postalCode;
    const isUSA = addr.country === 'USA';
    const usaInvalid = isUSA && (!addr.state || !/^\d{5}(?:-\d{4})?$/.test(postalCode));
    return baseInvalid || usaInvalid;
  }

  /** Restore focus to the element that triggered a modal, after the overlay has torn down. */
  private restoreFocus(el: HTMLElement | null): void {
    setTimeout(() => el?.focus(), FormSelectorComponent.FOCUS_RESTORE_DELAY_MS);
  }

  clearFieldValues(fc: any, ...keys: string[]) {
    if (!keys.length) return;

    const parent = fc.formControl?.parent;
    if (!parent) return;

    keys.forEach((key) => {
      const ctrl = parent.get(key);
      if (ctrl) {
        ctrl.setValue('');
        ctrl.markAsTouched();
      }
    });

    // Re-evaluate Formly expressions synchronously so conditional validators
    // (e.g. postalCode required/pattern for USA) reflect the new model state.
    let root = fc;
    while (root.parent) root = root.parent;
    root.options?.checkExpressions?.(root);

    // IMPORTANT: Re-emit valueChanges so the array-type modal Save button
    // re-evaluates its disabled state — do not remove.
    keys.forEach((key) => {
      const ctrl = parent.get(key);
      ctrl?.setValue(ctrl.value);
    });
  }

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected location: Location,
    protected LS: LoadingService,
    protected SS: SchemaService,
    protected CC: CompletenessCheckService,
    protected RL: RedlineService,
    protected OS: OverlayService,
    protected NS: NavigationDataService,
    protected IS: InstanceService,
    protected DS: DisplayRulesService,
    private http: HttpClient,
    private addressHelperService: AddressHelperService,
  ) {
    super(route, router, location, SS, CC, RL, OS, NS, IS);
    this.loadReferenceData();
  }

  private async loadReferenceData() {
    try {
      const [states, countries] = await Promise.all([
        firstValueFrom(
          this.http.get<any>(
            `${environment.urls.apiReference}/reference-data?contentType=application/schema+json&referenceKey=STATES&activeData=false`,
          ),
        ),
        firstValueFrom(
          this.http.get<any>(
            `${environment.urls.apiReference}/reference-data?contentType=application/schema+json&referenceKey=COUNTRIES&activeData=false`,
          ),
        ),
      ]);
      this.stateOptions.push(...(states?.oneOf || []).map((item) => ({ value: item.const, label: item.title })));
      this.countryOptions.push(...(countries?.oneOf || []).map((item) => ({ value: item.const, label: item.title })));
    } catch (error) {
      console.error('Failed to load reference data:', error);
    } finally {
      this.referenceDataLoaded = true;
    }
  }

  ngOnInit(): void {
    this.formInfo$ = this.getFormInfo().pipe(
      map((formInfo) => {
        const params = this.route.snapshot.paramMap;
        const sectionId = params.get('sectionId');
        const licenseTypeCode = params.get('licenseTypeCode');
        const viewType = params.get('viewType') as ViewType;
        const navId = sectionId + (licenseTypeCode ? `;${licenseTypeCode}` : '');
        this.IS.viewType = viewType;
        this.sectionData = this.NDS.getNavItem(
          `${viewType}.${this.route.snapshot.data.instanceData?.instance?.instanceMetadata?.attributes?.filingTypeCode}`,
          navId,
        );

        // Append attestation card at bottom of each formView page for submitted/processed filings
        if (
          viewType === 'formView' &&
          (this.permissions$$()?.displayedStatus === 'Filing Processed' ||
            this.permissions$$()?.displayedStatus === 'Submitted')
        ) {
          formInfo.layout = [
            ...formInfo.layout,
            [
              {
                type: 'title',
                props: {
                  label: 'Attestation',
                  size: 'medium',
                },
              },
            ],
            {
              template: 'attestation',
            },
          ];
        }

        return formInfo;
      }),
    );
  }

  setOptions(params: ParamMap, data: any, showRedlines) {
    const sectionId = params.get('sectionId');
    const viewType = params.get('viewType');

    const {
      instance: {
        instanceMetadata: md,
        collectedData: cd,
        id,
        templateName: tn,
        templateNamespace: tns,
        templateVersion: tv,
      },
      supportingData: sd,
    } = data.instanceData;

    const self = this;
    const options = {
      ...data.user,
      context: {
        disabled: viewType === 'formView' || this.hasReadOnlyMode$$() ? true : undefined,
        isLegalView: viewType === 'formView',
      },
      gatewayOrigin: environment.gatewayOrigin,
      referenceData: {
        states: this.stateOptions,
        countries: this.countryOptions,
      },
      isCompanyUser: this.DS.userPermissions.isCompanyUser,
      templateName: tn,
      templateVersion: tv,
      namespace: tns,
      instanceId: data.instanceData?.instance.id,
      datacollectionUrl: environment.urls.datacollection,
      filingTypeCode: data.instanceData?.instance?.instanceMetadata?.attributes?.filingTypeCode,
      currentInstance: this.IS.currentInstance,
      originalInstance: this.IS.getInitialInstance(),
      get permissions() {
        return self.DRS.displayRulesSubj.getValue();
      },
      hasReadOnlyMode: this.hasReadOnlyMode$$(),
      showRedlines,
      showValidation: this.showValidationOnCC$$() ?? false,
      subjectId:
        this.IS.currentInstance?.instanceMetadata?.target?.['subjectId'] ||
        this.IS.currentInstance?.instanceMetadata?.target?.['entityId'] ||
        '-',
      applicantName: this.IS.currentInstance?.collectedData?.identifyingInformation?.applicantName || '-',
      getBranchName: () => self.IS.branchName || '-',
      getMobileScreenSize: this.getMobileScreenSize.bind(this),
      // Formats an address into the two-line card block (state/country as codes) via the
      // shared rampdi-forms-common formatAddress() util (same engine MU1/MU4 use) instead of
      // brittle inline HTML concatenation in layout expressions. NMLSMOD-19405.
      formatAddressBlock: (address: any) => this.formatAddressBlock(address),
      // --- formState functions required by individualInformation layout ---
      getLegalName: (model) =>
        `${model?.firstName ?? ''} ${model?.middleName ? model.middleName + ' ' : ''}${model?.lastName ?? ''}${model?.suffix ? ' ' + model.suffix : ''}`,
      checkNameChange: (fieldConfig) => {
        return (
          JSON.stringify(fieldConfig.model?.personalInformation?.individualName) !==
          JSON.stringify(
            this.IS.getInitialInstance()?.collectedData?.identifyingInformation?.personalInformation?.individualName,
          )
        );
      },
      editMainAddress: (fieldConfig) => {
        this.openAddressEditModal(fieldConfig, 'mainAddress');
      },
      editMailingAddress: (fieldConfig) => {
        this.openAddressEditModal(fieldConfig, 'mailingAddress');
      },
      stampWebAddress: (currentData, model) => {
        if (!currentData.webAddressItemId) {
          const items = model?.webAddressItems || [];
          const maxFromItems = items.reduce((max, item) => Math.max(max, item?.webAddressItemId || 0), 0);
          const maxFromField = model?.webAddressMaxId || 0;
          const nextNumber = Math.max(maxFromItems, maxFromField) + 1;
          currentData.webAddressItemId = nextNumber;
          if (model) {
            model.webAddressMaxId = nextNumber;
          }
          if (this.IS.currentInstance?.collectedData?.webAddresses) {
            this.IS.currentInstance.collectedData.webAddresses.webAddressMaxId = nextNumber;
          }
        }
        if (!currentData.id) {
          currentData.id = crypto.randomUUID();
        }
      },
      stampExpenseInfo: (currentData, model) => {
        // Normalize: when the country is not USA, state must be blank. The UI
        // clearing (clearFieldValues sets '', clearOnDisabled resets null) can be
        // bypassed inside the nested array modal, leaving a stale state value that
        // fails the `expenseStateBlankWhenNotUsa` completeness check. Enforce it here
        // on save so the persisted model is always consistent.
        if (currentData?.address && currentData.address.country !== 'USA') {
          currentData.address.state = null;
        }
        if (!currentData.expenseInfoItemId) {
          const expenseInfo = model?.expenseInformation;
          const items = expenseInfo?.expenseInfoItems || [];
          const maxFromItems = items.reduce((max, item) => Math.max(max, item?.expenseInfoItemId || 0), 0);
          const maxFromField = expenseInfo?.expenseInfoMaxId || 0;
          const nextNumber = Math.max(maxFromItems, maxFromField) + 1;
          currentData.expenseInfoItemId = nextNumber;
          if (expenseInfo) {
            expenseInfo.expenseInfoMaxId = nextNumber;
          }
          if (this.IS.currentInstance?.collectedData?.expenseInformation) {
            this.IS.currentInstance.collectedData.expenseInformation.expenseInfoMaxId = nextNumber;
          }
        }
        if (!currentData.id) {
          currentData.id = crypto.randomUUID();
        }
      },
      getLearnMoreLink: (_sectionName: string) => '',
      isFormInvalid: (form) => {
        const model = form?.getRawValue?.() || form?.value;
        if (!model) return true;
        if (this.isAddressFormInvalid(model)) return true;
        if ('individualName' in model && !model.individualName) return true;
        if ('separatelyLicensed' in model && model.separatelyLicensed == null) return true;
        if (model.separatelyLicensed === true && !model.hasExpensesExplanation) return true;
        return false;
      },
      clearFieldValues: (fc, ...keys) => {
        this.clearFieldValues(fc, ...keys);
      },
      onHasExpensesChange: (fc) => {
        const items = this.IS.currentInstance?.collectedData?.expenseInformation?.expenseInfoItems;
        if (fc.formControl.value === false && items?.length > 0) {
          fc.props.overlayService
            .open('custom', {
              theme: 'warning',
              title: 'Warning',
              description: 'Changing the selection to "No" will remove the entered Expense information.',
              buttons: [
                {
                  name: 'Cancel',
                  value: false,
                  theme: 'secondary',
                  'aria-label': 'Cancel expense information removal',
                },
                { name: 'Ok', value: true, theme: 'primary', 'aria-label': 'Confirm expense information removal' },
              ],
            })
            .afterClosed$.toPromise()
            .then((e) => {
              if (e.data) {
                if (this.IS.currentInstance?.collectedData?.expenseInformation) {
                  this.IS.currentInstance.collectedData.expenseInformation.expenseInfoItems = [];
                }
                this.IS.addDataChangesToInstance([], ['expenseInformation.expenseInfoItems']);
                this.router.navigateByUrl(this.router.routerState.snapshot.url);
              } else {
                fc.formControl.setValue(true);
              }
            });
        }
      },
      clearFromControl: (fc, key) => {
        fc.form?.controls?.[key]?.setValue('');
      },
      onAddWebAddress: (component) => {
        const currentItems = this.IS.currentInstance?.collectedData?.webAddresses?.webAddressItems || [];
        const initialItems = this.IS.getInitialInstance()?.collectedData?.webAddresses?.webAddressItems || [];
        const allItems = [...currentItems, ...initialItems];
        const maxId = Math.max(0, ...allItems.map((item: any) => item.webAddressItemId || 0));
        component.showAddForm({ transactingBusiness: false, webAddressItemId: maxId + 1 });
      },
      /**
       * Auto-corrects "wwww" to "www" in a web address before saving.
       * Called via beforeSaveHook from the webAddresses array layout.
       * NMLSMOD-19503
       */
      correctWebAddress: (currentData, model) => {
        if (currentData?.webAddress) {
          currentData.webAddress = currentData.webAddress.replace(/wwww/gi, 'www');
        }
        options.stampWebAddress(currentData, model);
      },
      onAddExpenseInformation: (component) => {
        const currentItems = this.IS.currentInstance?.collectedData?.expenseInformation?.expenseInfoItems || [];
        const initialItems = this.IS.getInitialInstance()?.collectedData?.expenseInformation?.expenseInfoItems || [];
        const allItems = [...currentItems, ...initialItems];
        const maxId = Math.max(
          this.IS.currentInstance?.collectedData?.expenseInformation?.expenseInfoMaxId || 0,
          ...allItems.map((item: any) => item.expenseInfoItemId || 0),
        );
        component.showAddForm({ separatelyLicensed: null, hasExpensesExplanation: '', expenseInfoItemId: maxId + 1 });
      },
      determineModalTitle: (mode, value) => {
        switch (mode) {
          case 'add':
            return `Add ${value}`;
          case 'edit':
            return `Edit ${value}`;
          case 'delete':
            return `Delete ${value}`;
          default:
            return `View ${value}`;
        }
      },
      editIndividualName: (_fc) => {},
      enableNotificationEmail: (_fc) => {},
      updateNotificationEmail: (_component, _event) => {},
      openUpdateNotificationEmailModal: (fieldConfig) => {
        let emailValue: string = fieldConfig.model?.contactInformation?.notificationEmailAddress ?? '';
        const $disabled = new BehaviorSubject(!this.isValidEmail(emailValue));
        const triggerEl = document.activeElement as HTMLElement | null;

        this.OS.open(this.updateEmailModal, {
          theme: 'info',
          title: 'Update NMLS Notification Email',
          templateData: {
            email: emailValue,
            disabled: $disabled,
            onInput: (val: string) => {
              emailValue = val;
            },
          },
          buttons: [
            { name: 'Cancel', value: false, theme: 'secondary', 'aria-label': 'Cancel' },
            {
              name: 'Save',
              value: true,
              theme: 'primary',
              isDisabled: $disabled.asObservable(),
              'aria-label': 'Save notification email',
            },
          ],
        })
          .afterClosed$.toPromise()
          .then((response) => {
            this.restoreFocus(triggerEl);
            if (!response?.data || !this.isValidEmail(emailValue)) return;
            if (!fieldConfig.model.contactInformation) fieldConfig.model.contactInformation = {};
            fieldConfig.model.contactInformation.notificationEmailAddress = emailValue;
            const notifControl = fieldConfig.form?.get('contactInformation.notificationEmailAddress');
            if (notifControl) {
              notifControl.setValue(emailValue, { emitEvent: true });
            }
          });
      },
      handleAddUserSuffix: (_fieldConfig, _value) => {},
      handleAddUserSuffixViewMode: (_fieldConfig, _value) => {},
      updateIDVStatus: (_context?) => {},
      wrapString: (str: string, max: number) => this.wrapString(str, max),
      canViewGovtId: false,
      canViewPassportNumber: false,
      editEmail: false,
      hideHeaderForRegulator: this.hideHeaderForRegulator.bind(this),
      getLoggedInUserName: () => {
        const info = this.GC.store().userInfo;
        return info?.firstName ? `${info.firstName} ${info.lastName}` : '';
      },
      createdDate: formatDate(
        this.IS.currentInstance?.instanceMetadata?.dates?.created?.split('T')[0],
        'MM/DD/YYYY',
        'YYYY-MM-DD',
      ),
      submittedDate: formatDate(
        this.IS.currentInstance?.instanceMetadata?.dates?.submitted?.split('T')[0],
        'MM/DD/YYYY',
        'YYYY-MM-DD',
      ),
      // --- formState functions required by attestAndSubmit layout ---
      attestResp: this.canAttestResp$$(),
      userHasTitle: (() => {
        const entityId = this.IS.currentInstance?.instanceMetadata?.target?.['entityId'] ?? '';
        const company = this.DS.profileData?.associations?.find((assoc) => assoc?.entityId === entityId);
        return !!company?.title;
      })(),
      userProfileUrl: `${environment.gatewayOrigin}${environment.dxtBasePath}/mod-profile`,
      getCanRequestAttestation: (formState) => {
        const hasError = formState?.attestResp?.hasError;

        let message = '';
        if (hasError) {
          message =
            '<p>Clear all outstanding completeness check errors. Select the completeness check message to access the section of the filing that needs to be completed or corrected.</p>';
        } else {
          message = '<p>All completeness checks are clear. You can submit the filing below.</p>';
        }
        return [{ text: message }];
      },
      getAttestationFormContent: () => {
        const styles = 'font-weight: bold; text-decoration:underline;';
        const date = this.getDateFormatForAttestForm();
        const entityId = this.IS.currentInstance?.instanceMetadata?.target?.['entityId'] ?? '-';
        const userInfo = this.DS.profileData;
        const company = userInfo?.associations?.find((assoc) => assoc?.entityId === entityId);
        const fullName = this.formatLegalName(userInfo);
        const userTitle = company?.title || '';
        const userProfileUrl = `${environment.gatewayOrigin}${environment.dxtBasePath}/mod-profile`;
        const editLink = `<a href="${userProfileUrl}" target="_blank" rel="noopener" aria-label="Edit name and title in User Profile (opens in a new tab)" style="padding-left: 5px;">(edit)</a>`;
        const titleDisplay = userTitle ? `, <span style='${styles}'>${userTitle}</span>` : '';

        const intro = `I, <span style='${styles}'>${fullName}${titleDisplay}</span>${editLink}, am employed by or am an officer or a control person of <span style='${styles}'>${company?.entityName ?? '-'} (${entityId})</span> (Applicant). Applicant agrees to and represents the following:`;

        const formContent =
          '<br/><br/>(1) That the information and statements contained herein, including exhibits attached hereto, and other information filed herewith, all of which are made a part of this application, are current, true and complete and are made under the penalty of perjury, or un-sworn falsification to authorities, or similar provisions as provided by law;' +
          '<br/><br/>(2) To the extent any information previously submitted is not amended, such information remains accurate and complete;' +
          '<br/><br/>(3) To the extent any information submitted is part of an advance change notice with a delayed effective date, such information is accurate and complete as of this submission;' +
          '<br/><br/>(4) That the jurisdiction(s) to which an application is being submitted may conduct any investigation into the background of the Applicant, and any related individuals or entities, in accordance with all laws and regulations for purposes of making a determination on the application;' +
          '<br/><br/>(5) To keep the information contained in this form current and to file accurate supplementary information on a timely basis; and' +
          '<br/><br/>(6) To comply with the provisions of law, including the maintenance of accurate books and records, pertaining to the conduct of business for which the Applicant is applying.' +
          '<br/><br/>If the Applicant has knowingly made a false statement of a material fact in this application or in any documentation provided to support the foregoing application, then the foregoing application may be denied.' +
          `<br/><br/>On this <span style='${styles}'>${date}</span>, I verify that I am the named person above and that I am authorized to attest to and submit this filing on behalf of the Applicant. I solemnly swear (or affirm) under the penalty of perjury or un-sworn falsification to authorities, or similar provisions as provided by law that I have reviewed the foregoing responses, have made diligent inquiry as to their accuracy, and they are true and correct to the best of my knowledge, information and belief.`;

        return [{ text: `${intro} ${formContent}` }];
      },
      handleVerification: (fieldConfig) => {
        this.DS.sentIsVerifiedData(fieldConfig?.formControl?.value);
      },
    };

    this.IS.setdataforAll(options);
    return options;
  }

  hideHeaderForRegulator() {
    return this.DRS?.isRegulatorUser?.value ? 'hidden' : '';
  }

  wrapString(str: string, maxLength: number): string {
    // const regex = new RegExp(`.{1,${maxLength}}`, 'g');
    // return (str.match(regex) ?? []).join('\n');
    return str.replace(new RegExp(`(.{${maxLength}})`, 'g'), `$1\u200B`);
  }

  getMobileScreenSize(event?) {
    if (window.innerWidth < 1140) {
      return true;
    } else {
      return false;
    }
  }

  isValidEmail(v: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  /**
   * Formats an address into the two-line card block (state/country as codes, MU1/MU4 parity).
   * Delegates to the pure formatAddressBlock() util in address.utils so it can be tested without a component.
   * NMLSMOD-19405.
   */
  formatAddressBlock(address: any): string {
    return formatAddressBlockUtil(address);
  }

  onDataChange(data: any) {
    data = this.trimObjectValue(data);
    this.IS.addDataChangesToInstance(data, this.sectionData?.modelPath || [this.sectionData?.id]);
  }

  refreshFormData() {
    this.formInfo$ = this.getFormInfo().pipe(
      map((formInfo) => {
        const params = this.route.snapshot.paramMap;
        const sectionId = params.get('sectionId');
        const licenseTypeCode = params.get('licenseTypeCode');
        const viewType = params.get('viewType') as ViewType;
        const navId = sectionId + (licenseTypeCode ? `;${licenseTypeCode}` : '');
        this.IS.viewType = viewType;
        this.sectionData = this.NDS.getNavItem(
          `${viewType}.${this.route.snapshot.data.instanceData?.instance?.instanceMetadata?.attributes?.filingTypeCode}`,
          navId,
        );
        return formInfo;
      }),
    );
  }

  onStatusChange(status) {
    if (!this.hasReadOnlyMode$$()) {
      globalThis.finformUnsavedChanges = this.IS.hasUnsavedChanges([]);
    }
  }

  private trimObjectValue(obj: any, visited = new WeakSet()) {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }
    if (visited.has(obj)) {
      return obj;
    }
    visited.add(obj);
    const result: any = Array.isArray(obj) ? [] : {};
    for (const key in obj) {
      const value = obj[key];
      if (typeof value === 'string') {
        result[key] = value.trim();
      } else if (typeof value === 'object' && value !== null) {
        result[key] = this.trimObjectValue(value, visited);
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  private getDateFormatForAttestForm(): string {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    const rawSignedOn =
      this.IS.currentInstance?.instanceMetadata?.signatures?.indvlAcknowledgementConsent?.['signedOn'];
    const dateStr = typeof rawSignedOn === 'string' ? rawSignedOn.split('T')[0] : undefined;
    const signedOn = new Date(dateStr).toLocaleDateString('en-US', options);
    return signedOn !== 'Invalid Date' ? signedOn : new Date().toLocaleDateString('en-US', options);
  }

  private formatLegalName(profile: any): string {
    if (!profile) return '-';
    const nameParts = [profile?.firstName, profile?.middleName, profile?.lastName, profile?.suffix];
    return (
      nameParts
        .filter((part) => part)
        .join(' ')
        .trim() || '-'
    );
  }

  getSnapShot() {
    return getDeepestRouteSnapshot(this.route)?.data;
  }

  openAddressEditModal(fieldConfig: any, addressType: 'mainAddress' | 'mailingAddress') {
    const currentAddress = fieldConfig.model?.identifyingInformation?.[addressType] || {};
    const editModel = { ...currentAddress };
    const isMailingAddress = addressType === 'mailingAddress';

    // Capture the element that triggered the modal synchronously, before the async
    // schema/layout fetch -- otherwise document.activeElement resets to <body> and
    // focus would not return to the button the user activated.
    const triggerEl = document.activeElement as HTMLElement | null;

    const baseUrl = environment.versions['0.0.1'].urls;
    const layoutFile = isMailingAddress ? 'mailingAddressEdit.layout.json' : 'mainAddressView.layout.json';

    forkJoin({
      schema: this.http.get<any>(`${baseUrl.schemasBase}/addressEdit.json`),
      layout: this.http.get<any>(`${baseUrl.layoutsBase}/${layoutFile}`),
    }).subscribe({
      next: ({ schema, layout }) => {
        // Inject reference data options into select fields
        for (const row of layout) {
          const items = Array.isArray(row) ? row : [row];
          for (const field of items) {
            if (field.key === 'country') field.props = { ...field.props, options: this.countryOptions };
            if (field.key === 'state') field.props = { ...field.props, options: this.stateOptions };
            // Attach the control-character (PDF-paste) validator to the free-text
            // address fields. This nested fin-form does not inherit the host app's
            // global CONTROL_CHARS_VALIDATOR override, so it must be wired here.
            // fin-form layout `validators` must be an ARRAY of { name, validation, message }.
            if (['street1', 'street2', 'city', 'postalCode'].includes(field.key)) {
              field.validators = [...(field.validators ?? []), ...CONTROL_CHARS_LAYOUT_VALIDATORS];
            }
          }
        }

        this.addressFormData = { ...editModel };

        // Overlay ref for programmatic close from the save-based AV handlers (assigned after OS.open).
        let mailingOverlayRef: { close?: (data?: unknown) => void } | undefined;

        // Commits editModel to the instance and closes the modal (programmatic save path).
        const commitAndClose = () => {
          this.addressFormData = { ...editModel };
          if (typeof mailingOverlayRef?.close === 'function') {
            mailingOverlayRef.close(true);
          }
        };

        // AV is triggered on Save (not blur); Accept/Reject auto-save and close. Mailing path only.
        const mailingAvOptions = isMailingAddress
          ? {
              setMailingAddresses: (field, item) => {
                this.addressHelperService.populateAddressFields(field, item, '');
              },
              personalInformationAcceptSuggestedAddress: (field) => {
                const addressField = this.resolveMailingAddressField(field);
                const accepted = this.addressHelperService.acceptSuggestedAddress(addressField, '');
                if (accepted) {
                  this.syncMailingMetadata(editModel, addressField);
                  setTimeout(() => commitAndClose(), FormSelectorComponent.AUTO_SAVE_DELAY_MS);
                }
              },
              personalInformationRejectSuggestedAddress: (field) => {
                const addressField = this.resolveMailingAddressField(field);
                const hasWarning = this.addressHelperService.rejectSuggestedAddress(addressField, '');
                setTimeout(() => {
                  if (!hasWarning) {
                    this.syncMailingMetadata(editModel, addressField);
                    commitAndClose();
                  }
                }, FormSelectorComponent.AUTO_SAVE_DELAY_MS);
              },
              personalInformationClearVerificationStatus: (field) => {
                this.addressHelperService.clearOptions(field, '');
                this.syncMailingMetadata(editModel, field);
              },
              mailingAddressSave: (field) => {
                const addressField = this.resolveMailingAddressField(field);
                this.addressHelperService.verifyAddressOnSave(addressField, '').then((result) => {
                  this.syncMailingMetadata(editModel, addressField);
                  if (!result || result.action === 'proceed') {
                    commitAndClose();
                  }
                });
              },
              mailingAddressCancel: () => {
                if (typeof mailingOverlayRef?.close === 'function') {
                  mailingOverlayRef.close(false);
                }
              },
              personalInformationCountryChange: (field) => {
                // Preserve existing MU3 behavior: country swap clears BOTH postalCode and state.
                this.clearFieldValues(field, 'postalCode', 'state');
                this.addressHelperService.clearOptions(field, '');
                this.syncMailingMetadata(editModel, field);
              },
              personalInformationStateChange: (field) => {
                // state change only clears status so the next Save re-verifies.
                this.addressHelperService.clearOptions(field, '');
                this.syncMailingMetadata(editModel, field);
              },
              addressLookupEndpoint: environment.urls.addressLookup,
              // Seed lastUserVerifiedAddress so an unchanged persisted address is not re-verified on Save.
              ...this.addressHelperService.initAddressOptions('', {
                allowPOBox: true,
                lastUserVerifiedAddress: editModel.street1
                  ? {
                      street1: editModel.street1,
                      street2: editModel.street2,
                      city: editModel.city,
                      state: editModel.state,
                      postalCode: editModel.postalCode,
                      country: editModel.country,
                    }
                  : null,
              }),
            }
          : {};

        // Single shared options object: fin-form formState and copyMainAddress mutate the SAME reference.
        const formOptions: Record<string, unknown> = {
          clearFieldValues: this.clearFieldValues.bind(this),
          ...mailingAvOptions,
        };

        const overlayRef = this.OS.open(this.addressEditModal, {
          title: isMailingAddress
            ? editModel.street1
              ? 'Edit Mailing Address'
              : 'Add Mailing Address'
            : 'View Main Address',
          theme: 'info',
          size: 'xl',
          templateData: {
            form: {
              schema,
              layout,
              data: editModel,
              // fin-form spreads [options] directly as formState (shallow copy). The AV handlers and
              // suggestedAddress must sit flat on this object so the layout's
              // formState?.mailingAddressSave / formState?.suggestedAddress expressions resolve.
              // Do NOT nest under a `formState` key -- that double-wraps and breaks Save/Cancel.
              options: formOptions,
            },
            isMailingAddress,
            copyMainAddress: () => {
              const mainAddr = this.IS.currentInstance?.collectedData?.identifyingInformation?.mainAddress;
              if (!mainAddr) return;
              Object.assign(editModel, {
                street1: mainAddr.street1 ?? '',
                street2: mainAddr.street2 ?? '',
                city: mainAddr.city ?? '',
                state: mainAddr.state ?? '',
                country: mainAddr.country ?? '',
                postalCode: mainAddr.postalCode ?? '',
                // Copy resets AV metadata to unverified so a stale 'suggested' does not block Save.
                addressVerificationStatus: AddressVerificationStatus.UNVERIFIED,
                poBoxIndicator: false,
                addressType: 'U',
              });
              // formOptions is the SAME reference fin-form uses as formState, so clearing reaches the card.
              const fs = formOptions as {
                suggestedAddress?: unknown;
                addressUndeliverableMessage?: unknown;
                lastUserVerifiedAddress?: unknown;
              };
              fs.suggestedAddress = null;
              fs.addressUndeliverableMessage = null;
              fs.lastUserVerifiedAddress = null;
              this.addressFormData = { ...editModel };
            },
            onDataChange: (event) => {
              Object.assign(editModel, event);
            },
            onStatusChanges: () => {
              // No-op: Save-disabled is driven by the layout expression (form validity + suggested status).
            },
          },
          buttons: isMailingAddress
            ? []
            : [{ name: 'Close', value: false, theme: 'primary', 'aria-label': 'Close address view' }],
        });

        // expose overlay ref to save-based AV handlers for programmatic close.
        mailingOverlayRef = overlayRef as unknown as { close?: (data?: unknown) => void };

        // The design system's finra-modal #content shows a scrollbar even when content fits.
        // Use a ResizeObserver to dynamically show/hide the scrollbar based on actual overflow.
        if (isMailingAddress) {
          let resizeObs: ResizeObserver | null = null;
          const observer = new MutationObserver(() => {
            const modal = document.querySelector('.templatetype finra-modal');
            if (modal?.shadowRoot) {
              // Hide the now-empty native commands slot (Save/Cancel moved inline into the form layout).
              if (!modal.shadowRoot.querySelector('.hide-empty-commands')) {
                const style = document.createElement('style');
                style.className = 'hide-empty-commands';
                style.textContent = '#commands, slot[name="commands"] { display: none !important; }';
                modal.shadowRoot.appendChild(style);
              }
              // Save/Cancel footer styling lives in form-selector.component.scss so it survives
              // fin-form re-rendering the layout (e.g. after Copy Main Address).
              const btnRow = document.querySelector('.mailing-address-cancel')?.closest('.formly-layout-row');
              const content = modal.shadowRoot.querySelector('#content') as HTMLElement;
              if (content && !resizeObs) {
                const updateOverflow = () => {
                  content.style.overflowY = content.scrollHeight > content.clientHeight ? 'auto' : 'hidden';
                };
                updateOverflow();
                resizeObs = new ResizeObserver(updateOverflow);
                resizeObs.observe(content);
              }
              // Keep observing until the inline button row (renders after the modal shell) has rendered.
              if (btnRow && content) {
                observer.disconnect();
              }
            }
          });
          observer.observe(document.body, { childList: true, subtree: true });
          firstValueFrom(overlayRef.afterClosed$).then(() => {
            observer.disconnect();
            resizeObs?.disconnect();
          });
        }

        firstValueFrom(overlayRef.afterClosed$).then((result) => {
          document.documentElement.style.overflow = '';
          document.body.style.overflow = '';
          this.restoreFocus(triggerEl);
          if (result?.data) {
            if (!this.IS.currentInstance.collectedData.identifyingInformation) {
              this.IS.currentInstance.collectedData.identifyingInformation = {};
            }
            this.IS.currentInstance.collectedData.identifyingInformation[addressType] = { ...editModel };

            this.IS.addDataChangesToInstance(this.IS.currentInstance.collectedData.identifyingInformation, [
              'identifyingInformation',
            ]);

            this.refreshFormData();
          }
        });
      },
      error: (err) => {
        console.error('Failed to load address edit form:', err);
      },
    });
  }

  // finds a real address input field (with a live formControl) so verification drives formly model sync.
  // Walks to the field tree root, then returns the street1 (or city) control; falls back to the passed
  // field if the tree is malformed so AV handlers still receive a usable field rather than undefined.
  private resolveMailingAddressField(field: FormlyFieldConfig): FormlyFieldConfig {
    let root: FormlyFieldConfig | undefined = field;
    while (root?.parent) root = root.parent;
    const flatten = (fields: FormlyFieldConfig[], acc: FormlyFieldConfig[] = []): FormlyFieldConfig[] => {
      (fields || []).forEach((f) => {
        if (!f) return;
        acc.push(f);
        if (f.fieldGroup) flatten(f.fieldGroup, acc);
        if ((f as { fields?: FormlyFieldConfig[] }).fields) flatten((f as { fields: FormlyFieldConfig[] }).fields, acc);
      });
      return acc;
    };
    const all = flatten(root?.fieldGroup || (root as { fields?: FormlyFieldConfig[] })?.fields || []);
    return (
      all.find((f) => f?.key === 'street1' && f?.formControl) ||
      all.find((f) => f?.key === 'city' && f?.formControl) ||
      field
    );
  }

  // copies verification metadata (status/poBox/type) from form controls into editModel.
  // MU3's mailing address is always the standalone flat modal (empty pathPrefix) -- unlike MU4's
  // nested array sections -- so controls are read by bare name rather than a threaded buildFormPath.
  private syncMailingMetadata(editModel: any, addressField: any): void {
    const form = addressField?.form;
    if (!form) return;
    const val = (name: string) => form.get(name)?.value;
    const status = val('addressVerificationStatus');
    const poBox = val('poBoxIndicator');
    const type = val('addressType');
    if (status !== undefined && status !== null) editModel.addressVerificationStatus = status;
    if (poBox !== undefined && poBox !== null) editModel.poBoxIndicator = poBox;
    if (type !== undefined && type !== null) editModel.addressType = type;
  }

  ngOnDestroy(): void {
    this.isDestroying$.next(undefined);
    this.isDestroying$.complete();
    this.addressCardObserver?.disconnect();
  }

  private addressCardObserver: MutationObserver | null = null;

  ngAfterViewInit(): void {
    this.observeSuggestAddressCards();
  }

  /**
   * Injects styles into the Shadow DOM of the suggested-address card and its
   * accept/reject icons. finra-card / finra-icon use Shadow DOM, so normal
   * component CSS cannot reach them -- a MutationObserver watches for the cards
   * appearing (they are conditionally rendered) and injects a scoped <style>.
   * Ported from the working MU1/MU4 implementation for consistency.
   */
  private observeSuggestAddressCards(): void {
    const styleSuggestAddressCard = (el: HTMLElement) => {
      const sr = (el as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot;
      if (!sr || sr.querySelector('.suggest-address-style')) return;
      const style = document.createElement('style');
      style.className = 'suggest-address-style';
      style.textContent =
        '#container { background: #F0F6FC !important; border: 2px solid #0070B7 !important; border-radius: 4px !important; box-shadow: none !important; overflow: visible !important; } #content { background: #F0F6FC !important; padding: 12px !important; border-radius: 4px !important; }';
      sr.appendChild(style);
    };

    // Inject fill color into finra-icon shadow DOM for accept/reject buttons.
    const styleAddressIcon = (el: HTMLElement, color: string, guardClass: string) => {
      const sr = (el as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot;
      if (!sr || sr.querySelector(`.${guardClass}`)) return;
      const style = document.createElement('style');
      style.className = guardClass;
      style.textContent = `svg path { fill: ${color} !important; }`;
      sr.appendChild(style);
    };

    // Set text color on address accept/reject link buttons (override default blue).
    const styleAddressLinkText = (el: HTMLElement) => {
      if (el.dataset.linkStyled) return;
      el.style.paddingBottom = '0';
      el.style.paddingTop = '1.5em';
      el.style.color = 'var(--fin-slate-90, #11233e)';
      el.dataset.linkStyled = 'true';
    };

    const observedShadowRoots = new WeakSet<ShadowRoot>();
    let styleDebounce: ReturnType<typeof setTimeout>;

    const findAndStyleCards = (root: Document | ShadowRoot | HTMLElement) => {
      root.querySelectorAll('finra-card.suggest-address-container').forEach((el) => {
        styleSuggestAddressCard(el as HTMLElement);
        this.removeSuggestedAddressTextFocus(el as HTMLElement);
      });
      root.querySelectorAll('.address-accept finra-icon').forEach((el) => {
        styleAddressIcon(el as HTMLElement, '#29c566', 'address-accept-style');
      });
      root.querySelectorAll('.address-reject finra-icon').forEach((el) => {
        styleAddressIcon(el as HTMLElement, 'red', 'address-reject-style');
      });
      root.querySelectorAll('a.address-accept, a.address-reject').forEach((el) => {
        styleAddressLinkText(el as HTMLElement);
      });
      root.querySelectorAll('finra-modal, finra-card').forEach((el) => {
        const sr = (el as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot;
        if (sr && !observedShadowRoots.has(sr)) {
          observedShadowRoots.add(sr);
          findAndStyleCards(sr);
          addressCardObserver.observe(sr, { childList: true, subtree: true });
        }
      });
    };

    const addressCardObserver = new MutationObserver(() => {
      clearTimeout(styleDebounce);
      styleDebounce = setTimeout(() => findAndStyleCards(document), 50);
    });

    // Initial scan + observe the document body for the modal/cards appearing.
    findAndStyleCards(document);
    addressCardObserver.observe(document.body, { childList: true, subtree: true });
    this.addressCardObserver = addressCardObserver;
  }

  /**
   * 508 fix (NMLSMOD-20489-AV): remove the suggested "Preferred Address" text from the keyboard tab
   * order. The shared fin-message component renders description content inside a Shadow DOM
   * `<div tabindex="0">`, which makes non-interactive text a focus stop. We reach into each
   * fin-message shadow root within the suggested-address card and set tabindex="-1".
   *
   * We intentionally do NOT set aria-hidden -- the address must remain announced to screen readers;
   * only the redundant focus stop is removed. The Accept/Reject links live in the light DOM and are
   * not touched here, so they remain fully keyboard-operable.
   */
  private removeSuggestedAddressTextFocus(cardEl: HTMLElement): void {
    cardEl.querySelectorAll('fin-message').forEach((msg) => {
      const sr = (msg as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot;
      if (!sr) return;
      sr.querySelectorAll('div[tabindex="0"]').forEach((el) => {
        (el as HTMLElement).setAttribute('tabindex', '-1');
      });
    });
  }
}
